import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, take, tap, timer } from 'rxjs';

import { loadSystemUpgrade, selectSystemUpgrade } from 'src/app/stores/global';
import { selectSettingDeviceType } from 'src/app/stores/settings';

import { DEVICE_TYPES, DeviceType } from '../../interfaces/ISettings';
import { SystemUpgradeState, UmpService } from '../../services/ump';
import { TTW_SCREEN, UMP_POLL_INTERVAL_MS, VIEW_TYPES } from '../../utils/constants';

const DEVICE_TYPE_ROUTE: Record<DeviceType, string> = {
  [DEVICE_TYPES.NEXT_UP]: VIEW_TYPES.RETAIL,
  [DEVICE_TYPES.ORDER_HQ_FRONT]: VIEW_TYPES.FRONT_LOADING,
  [DEVICE_TYPES.ORDER_HQ_REAR]: VIEW_TYPES.REAR_LOADING,
  [DEVICE_TYPES.ORDER_HQ_FLEX]: VIEW_TYPES.RETAIL,
  [DEVICE_TYPES.ARRAY_FL]: VIEW_TYPES.FRONT_LOADING,
  [DEVICE_TYPES.ARRAY_RL]: VIEW_TYPES.REAR_LOADING,
};

@Injectable({
  providedIn: 'root',
})
export class SystemUpgradeService {
  private started = false;
  private redirectToTtw = false;
  private previousState: SystemUpgradeState | null = null;

  constructor(
    private store: Store,
    private router: Router,
    private umpService: UmpService,
    @Inject('Window') private window: Window
  ) {}

  init() {
    if (this.started) return; // singleton use
    if (!this.umpService.isConfigured()) return;
    this.started = true;

    this.initRedirectOnStateChange();
    this.initPolling();
  }

  private initRedirectOnStateChange() {
    this.store
      .select(selectSystemUpgrade)
      .pipe(distinctUntilChanged((a, b) => a.state === b.state))
      .subscribe(({ state }) => {
        if (state === this.previousState) {
          return;
        }
        const previous = this.previousState;
        this.previousState = state;

        if (state === SystemUpgradeState.IN_PROGRESS) {
          this.redirectToTtw = this.router.url.startsWith(TTW_SCREEN.URL);
        } else if (
          state === SystemUpgradeState.OPERATIONAL &&
          (previous === SystemUpgradeState.IN_PROGRESS || previous === SystemUpgradeState.FAILED)
        ) {
          this.redirectToDeviceRoute();
        }
      });
  }

  private initPolling() {
    timer(0, Number(UMP_POLL_INTERVAL_MS))
      .pipe(tap(() => this.store.dispatch(loadSystemUpgrade())))
      .subscribe();
  }

  private redirectToDeviceRoute(): void {
    if (this.redirectToTtw) {
      const url = this.router.serializeUrl(this.router.createUrlTree([TTW_SCREEN.URL]));
      this.window.location.replace(url);
      return;
    }

    this.store
      .select(selectSettingDeviceType)
      .pipe(take(1))
      .subscribe((deviceType) => {
        const route = DEVICE_TYPE_ROUTE[deviceType] ?? VIEW_TYPES.RETAIL;
        const url = this.router.serializeUrl(this.router.createUrlTree([route]));
        this.window.location.replace(url);
      });
  }
}
